import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { Save, ArrowLeft, ShieldCheck, Search, Trash2 } from 'lucide-react';
import AuditHistoryModal from './AuditHistoryModal';
import { usePermissions } from './usePermissions';
import { swalTheme, swalDangerColor } from '../lib/swalTheme';

const PrecautionDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const { canDelete } = usePermissions('precautions');
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const [data, setData] = useState({ precautionnum: '', description: '', siteid: session.site_id || '' });
  const [showAudit, setShowAudit] = useState(false);

  useEffect(() => {
    if (!isNew) axios.get(`/api/precautions/${id}`).then(res => setData(res.data));
  }, [id, isNew]);

  useEffect(() => {
    const openLog = () => setShowAudit(true);
    window.addEventListener('GLOBAL_LOG_HANDLER', openLog);
    return () => window.removeEventListener('GLOBAL_LOG_HANDLER', openLog);
  }, []);

  const handleSave = async () => {
    try {
      await axios.post('/api/precautions/save', data);
      toast.success(t('precautions.details.saveSuccess', 'Precaution Library Updated!'));
      if (isNew) navigate(`/precautions/${data.precautionnum}`);
    } catch (err) { toast.error(t('common.save_fail', 'Save failed')); }
  };

  const handleDelete = () => {
    Swal.fire({
      ...swalTheme(),
      title: t('common.are_you_sure', 'Are you sure?'), text: t('common.cannot_be_undone', 'This action cannot be undone!'),
      icon: 'warning', showCancelButton: true, confirmButtonColor: swalDangerColor(),
      confirmButtonText: t('common.yes_delete', 'Yes, delete it!')
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`/api/precautions/${data.precautionnum}`);
          toast.success(t('common.delete_success', 'Deleted successfully'));
          navigate('/precautions');
        } catch (err) { toast.error(err.response?.data?.detail || t('common.delete_fail', 'Delete failed')); }
      }
    });
  };

  useEffect(() => {
    const triggerDelete = () => handleDelete();
    window.addEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
    return () => window.removeEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
  }, [handleDelete]);

  return (
    <div className="flex flex-col h-screen bg-surface-app text-[11px] font-sans uppercase font-bold tracking-tighter">
      <div className="bg-surface-card p-2 border-b border-line flex justify-between items-center shadow-sm z-20 font-black">
        <div className="flex gap-6 ml-4">
          <button onClick={() => navigate('/precautions')} className="p-1 hover:bg-surface-panel rounded-full text-primary"><ArrowLeft size={18} /></button>
          <HeaderField label={t('precautions.fields.num', 'Num')} value={data.precautionnum} isBold width="w-32" disabled={!isNew} onChange={v => setData({...data, precautionnum: v})} />
          <HeaderField label={t('common.site', 'Site')} value={data.siteid} disabled width="w-24" />
        </div>
        <div className="flex items-center gap-2 mr-4">
          {!isNew && canDelete && (
            <button onClick={handleDelete} className="p-2 text-status-danger hover:bg-status-danger-bg rounded transition-colors" title={t('common.delete', 'Delete')}>
              <Trash2 size={16}/>
            </button>
          )}
          <button onClick={handleSave} className="bg-primary text-white px-8 py-2 rounded flex items-center gap-2 hover:bg-primary-dark shadow-lg">
             <Save size={14} /> {t('precautions.details.saveToLibrary', 'SAVE TO LIBRARY')}
          </button>
        </div>
      </div>
      <div className="flex-1 p-4 bg-surface-panel">
          <div className="max-w-4xl mx-auto">
            <SectionCard title={t('precautions.details.precautionDefinition', 'Precaution Definition')}>
               <div className="p-4 space-y-4">
                  <EditableField label={t('precautions.fields.precautionCode', 'Precaution Code')} value={data.precautionnum} disabled={!isNew} />
                  <div className="pt-2">
                    <label className="text-ink-secondary font-black text-[8px] uppercase tracking-widest italic mb-1 block">{t('precautions.fields.fullInstruction', 'Full Instruction Statement')}</label>
                    <textarea
                        className="w-full p-3 border border-line rounded outline-none font-black text-primary bg-surface-card"
                        rows="4" value={data.description}
                        onChange={e => setData({...data, description: e.target.value.toUpperCase()})}
                    />
                  </div>
               </div>
            </SectionCard>
          </div>
      </div>
      <AuditHistoryModal isOpen={showAudit} onClose={() => setShowAudit(false)} tableName="precautions" recordId={data.precautionnum} />
    </div>
  );
};

const HeaderField = ({ label, value, onChange, width, isBold, disabled }) => (
  <div className="flex flex-col border-r border-line px-4 last:border-0">
    <label className="text-[8px] text-ink-secondary font-black uppercase tracking-widest mb-0.5 italic">{label}</label>
    <input className={`${width} border-b border-transparent outline-none py-0.5 font-black uppercase ${isBold ? 'text-primary-dark text-[12px]' : 'text-primary'} bg-transparent focus:border-primary`}
           value={value || ''} disabled={disabled} onChange={e => onChange && onChange(e.target.value)} />
  </div>
);

const SectionCard = ({ title, children }) => (
  <div className="bg-surface-card border border-line rounded shadow-sm overflow-hidden transition-all hover:shadow-md">
    <div className="p-2 bg-surface-panel border-b border-line font-black text-ink-secondary uppercase text-[9px] tracking-widest flex items-center gap-2">
      <div className="w-1.5 h-3 bg-primary rounded-full"></div> {title}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const EditableField = ({ label, value, onChange, onSearch, type = "text", disabled }) => (
  <div className="flex flex-col gap-1 border-b border-line group focus-within:border-primary transition-all py-1">
    <label className="text-ink-secondary font-black text-[8px] uppercase tracking-widest italic">{label}</label>
    <div className="flex items-center justify-between pb-0.5">
      <input type={type} disabled={disabled} className="outline-none font-black text-primary bg-transparent w-full text-[11px]"
        value={value || ''} onChange={e => onChange && onChange(e.target.value)} />
      {onSearch && <Search size={12} className="text-primary cursor-pointer hover:text-primary-dark" onClick={onSearch} />}
    </div>
  </div>
);

export default PrecautionDetails;
